import { useState, useCallback, useMemo, useImperativeHandle, forwardRef, useContext } from 'react';
import { useTranslation, Trans } from 'react-i18next';

import { updateTask, removeTask } from 'actions/tasks';

import { getFormattedDuration } from 'utils/date';
import { isSubtaskTask, isPersonalTask } from 'utils/task';
import { isTempId } from 'utils/helpers';

import { useCurrentUserId } from 'shared-hooks/user';
import { useCanUser } from 'shared-hooks/permission';
import { useIsDependant } from 'shared-hooks/task';

import { PublicPageContext } from 'context/PublicPageContext';

import UserName from 'components/user-name';
import TaskAssignees from 'components/TaskAssignees';
import TaskGroupName from 'components/task-group-name';
import SelectTaskGroupPopout from 'components/SelectTaskGroupPopout';
import LabelsList from 'components/labels-list';
import TaskCompleteToggle from 'components/TaskCompleteToggle';
import MultiLineInput from 'components/MultiLineInput';
import NiftyMarkdown from 'components/nifty-markdown';
import CanUser from 'components/can-user';
import TaskDueDatePicker from 'components/TaskDueDatePicker';
import ProjectName from 'components/project-name';
import DateTime from 'components/DateTime';
import OverflowTooltip from 'components/overflow-tooltip';
import NotificationsBubble from 'components/NotificationsBubble';
import TaskTaskGroup from 'components/TaskTaskGroup';
import Tooltip from 'components/tooltip';
import SelectProjectPopout from 'components/SelectProjectPopout';
import { ResourceListItem } from 'components/ResourceList';
import TaskActions from 'components/TaskActions';
import TaskNotificationsBubble from 'components/TaskNotificationsBubble';

import IconStopwatch from 'icons/icon-stopwatch';
import IconCalendarSimple from 'icons/icon-calendar-simple';
import IconCheck from 'icons/icon-check';
import IconSubtask from 'icons/icon-subtask';
import IconTransfer from 'icons/icon-transfer';
import IconArrowDown from 'icons/icon-arrow-down';
import IconPlus from 'icons/icon-plus';
import IconProject from 'icons/icon-project';
import IconDiscussionAlt from 'icons/icon-discussion-alt';
import IconClose from 'icons/icon-close';
import IconInfo from 'icons/icon-info';
import IconListChecks from 'icons/icon-list-checks';
import IconMoveHandle from 'icons/icon-move-handle';
import IconStatus from 'icons/icon-status';


const TaskListItem = forwardRef(({ id, index, task, draggable, dragType, showComplete, editOnDoubleClick, height, showAssignees, showDueDateWhenEmpty, showProject, showActions = true, withTaskGroupIcon, avatarSize = 'xxsmall', namePlaceholder, noGutter, checkSubtasks = true, dontFetchDep, onUncomplete, onComplete, onDoubleClick, onRemoveFromMetting, onTaskGroupSelect, onClick, onNameChange, onRemove, actionsElement, moveItem }, ref) => {
    const { t } = useTranslation(['task', 'project', 'date']);
    const isSubtask = isSubtaskTask(task);
    const isPersonal = isPersonalTask(task);
    const tempId = isTempId(task?.id);
    const hasEditPermission = useCanUser(isSubtask ? 'editSubtask' : 'editTask', task?.created_by);
    const hasRemovePermission = useCanUser(isSubtask ? 'removeSubtask' : 'removeTask', task?.created_by);
    const hasCompletePermission = useCanUser(isSubtask ? 'completeSubtask' : 'completeTask', task?.created_by);
    const [isFocused, setIsFocused] = useState(false);
    const [isEditing, setIsEditing] = useState(false);
    const [isDependant] = useIsDependant(id, dontFetchDep);
    const isLocked = Boolean(!task?.completed && isDependant);
    const currentUserId = useCurrentUserId();
    const projectId = task?.project;
    const publicPageCtx = useContext(PublicPageContext);

    const canEdit = useMemo(() => {
        return id && !tempId && hasEditPermission;
    }, [id, tempId, hasEditPermission]);

    const canRemove = useMemo(() => {
        return id && !tempId && hasRemovePermission;
    }, [id, tempId, hasRemovePermission]);

    const beginEdit = useCallback(() => {
        if (!canEdit) {
            return;
        }

        setIsEditing(true);
    }, [canEdit]);

    const handleClick = () => {
        if (!tempId) {
            onClick?.(id);
        }
    }

    const handleToggleCompleteClick = useCallback(async (complete, taskGroupId) => {
        if (!complete) {
            if (onUncomplete) {
                return onUncomplete(id, taskGroupId);
            }

            await updateTask(id, {
                completed: false,
                completed_by: undefined,
                completed_on: undefined
            });
            return;
        }

        if (onComplete) {
            return onComplete(id);
        }

        await updateTask(id, {
            completed: true,
            completed_by: currentUserId,
            completed_on: new Date()
        });
    }, [id, currentUserId, onComplete, onUncomplete]);

    const handleTaskGroupSelect = (taskGroupId, projectId) => {
        onTaskGroupSelect?.(id, taskGroupId, projectId);
    }

    const handleAltTaskGroupSelect = ({ id: taskGroupId, projectId }) => {
        onTaskGroupSelect?.(id, taskGroupId, projectId);
    }

    const getActionItems = useCallback(() => {
        const result = [];

        if (task?.comments && onClick && publicPageCtx?.pageConfig?.tasks?.show_comments !== false) {
            result.push(
                <button className="btn btn-square btn-xxxsmall btn-simple btn-icon" onClick={handleClick}>
                    <IconDiscussionAlt size="10px" />
                </button>
            );
        }

        if (showAssignees && publicPageCtx?.pageConfig?.tasks?.show_assignees !== false) {
            result.push(
                <TaskAssignees
                    id={id}
                    avatarSize={avatarSize}
                    maxUsersCount={3}
                />
            );
        }

        if (showActions) {
            result.push(
                actionsElement || <TaskActions
                    id={id}
                    className="item-actions"
                    innerClassName="btn btn-square btn-xxxsmall btn-simple btn-icon item-click-action"
                />
            );
        }

        if (onRemoveFromMetting && canEdit) {
            result.push(
                <Tooltip tooltip="Remove from the meeting">
                    <span className="util task-remove-from-meeting item-click-action" onClick={onRemoveFromMetting}>
                        <span className="item-actions">
                            <IconClose className="icon-only" />
                        </span>
                    </span>
                </Tooltip>
            );
        }

        return result;
    }, [id, avatarSize, task?.comments, onClick, handleClick, showAssignees, showActions, onRemoveFromMetting, canEdit]);


    const getMetaItems = useCallback(() => {
        const result = [];

        if (showProject) {
            const content = projectId ? <ProjectName id={projectId} />  : t('project:Add to Project')

            result.push({
                icon: <IconProject />,
                content: onTaskGroupSelect && canEdit ? (
                    <SelectTaskGroupPopout
                        title={ t('project:Add to Project') }
                        projectId={projectId}
                        selected={task?.task_group}
                        xAlign="left"
                        defaultXPosition="right"
                        onSelect={handleAltTaskGroupSelect}
                    >
                        <div>
                            { content }
                        </div>
                    </SelectTaskGroupPopout>
                ) : content,
                isAction: onTaskGroupSelect && canEdit
            });
        }


        if (task?.nice_id && !isPersonal && !isSubtask) {
            result.push({
                content: task.nice_id
            });
        }


        if (task?.labels?.length) {
            result.push({
                content: <LabelsList labels={task.labels} inverted={true} />
            });
        }


        if (task?.completed && task?.completed_by) {
            result.push({
                icon: <IconCheck />,
                content: <Trans
                    i18nKey="date:Date by"
                    values={{ date: task?.completed_on }}
                    components={{
                        DateTime: <DateTime format="dateTime" />,
                        UserName: <UserName id={task?.completed_by} />
                    }}
                />
            });
        }

        if (task?.tracked_time) {
            result.push({
                icon: <IconStopwatch />,
                content: t('Elapsed: time', { time: getFormattedDuration(task.tracked_time) })
            });
        }

        if ((showDueDateWhenEmpty && canEdit) || task?.due_date) {
            result.push({
                icon: <IconCalendarSimple />,
                content: canEdit ? (
                    <>
                        {
                            task?.due_date ? (
                                <DateTime format="dateTimeShort">{task.due_date}</DateTime>
                            ) : t('common:Due Date')
                        }

                        <TaskDueDatePicker
                            id={id}
                            xAlign="right"
                            defaultXPosition="left"
                            showQuickOptions={true}
                        />
                    </>
                ) : <DateTime format="dateTimeShort">{task.due_date}</DateTime>,
                isAction: canEdit
            });
        }

        if (task?.total_subtasks) {
            result.push({
                icon: <IconSubtask />,
                content: typeof task.completed_subtasks === 'undefined' ? (
                    t('subtasks count', { count: task?.total_subtasks })
                ) : (
                    <>{ task.completed_subtask }/{ task?.total_subtasks }</>
                )
            });
        }
    
        if (task?.comments && publicPageCtx?.pageConfig?.tasks?.show_comments !== false) {
            result.push({
                content: task?.comments,
                icon: <IconDiscussionAlt />
            });
        }

        if (task?.description) {
            result.push({
                tooltip: task.description,
                icon: <IconInfo />
            });
        }

        if (task?.checklists?.length) {
            result.push({
                tooltip: t('checklist.tooltip'),
                icon: <IconListChecks />
            });
        }


        if (task?.task_group) {
            result.push({
                pushRight: true,
                isAction: onTaskGroupSelect,
                icon: withTaskGroupIcon ? <IconStatus /> : undefined,
                content: onTaskGroupSelect ? (
                    <TaskTaskGroup
                        id={id}
                        icon={<IconTransfer />}
                        withIcon={false}
                        onChange={handleTaskGroupSelect}
                        onComplete={handleToggleCompleteClick}
                    />
                ) : <TaskGroupName id={task.task_group} />
            });
        }

        return result;
    }, [id, t, showProject, showDueDateWhenEmpty, task, projectId, canEdit, isPersonal, isSubtask, withTaskGroupIcon, onTaskGroupSelect, handleToggleCompleteClick]);

    const handleDoubleClick = () => {
        if (editOnDoubleClick) {
            beginEdit();
        }

        onDoubleClick?.(id);
    }

    const handleInputFocus = () => {
        setIsFocused(true);
    }

    const handleInputBlur = nextName => {
        const nameChanged = !!(nextName !== '') || !!(task?.name !== nextName);

        setIsFocused(false);
        setIsEditing(false);

        if (!nameChanged) {
            return;
        }

        if (nextName === '') {
            if (canRemove) {
                if (onRemove) {
                    onRemove(id);
                } else {
                    removeTask(id);
                }
            }
        } else {
            if (canEdit) {
                if (onNameChange) {
                    onNameChange(id, nextName)
                } else {
                    updateTask(id, { name: nextName });
                }
            }
        }
    }

    const handleKeyDown = event => {
        if (event.which === 13 && !event.shiftKey) {
            event.preventDefault();
        }
    }

    const ctrl = useMemo(() => ({
        beginEdit
    }), [beginEdit]);

    useImperativeHandle(ref, () => ctrl, [ctrl]);

    return (
        <ResourceListItem
            id={id}
            index={index}
            completed={task?.completed}
            canEdit={canEdit}
            focused={isFocused}
            editing={isEditing}
            draggable={draggable && canEdit}
            dragType={dragType}
            noGutter={noGutter}
            markerElement={showComplete ? (
                <TaskCompleteToggle
                    id={id}
                    checkSubtasks={!isSubtask && checkSubtasks}
                    locked={isLocked}
                    completed={task?.completed}
                    projectId={projectId}
                    disabled={!hasCompletePermission}
                    selectListDisabled={isSubtask}
                    size="medium"
                    onChange={handleToggleCompleteClick}
                />
            ) : null}
            onClick={handleClick}
            onDoubleClick={handleDoubleClick}
            style={height ? { height } : undefined}
            getActionItems={getActionItems}
            getMetaItems={getMetaItems}
            moveItem={moveItem}
        >
            {
                !isEditing ? (
                    <>
                        <div className="item-title">
                            <NiftyMarkdown>{ task?.name }</NiftyMarkdown>
                        </div>

                        <TaskNotificationsBubble id={id} />
                    </>
                ) : (
                    <MultiLineInput
                        className="item-title"
                        canEdit={isEditing || task?.name === ''}
                        autoFocus={isEditing}
                        value={task?.name}
                        placeholder={namePlaceholder}
                        onFocus={handleInputFocus}
                        onBlur={handleInputBlur}
                        onKeyDown={handleKeyDown}
                    />
                )
            }
        </ResourceListItem>
    );
});

export default TaskListItem;
